<!-- Base.DatePicker.vue (single date) -->
<template>
  <div class="lms_dp">
    <div ref="inputRef" class="lms_dp_input" :class="{ filled: modelValue, active: open, error, disabled: props.disabled }">
      <input
        type="text"
        class="lms_dp_text"
        :value="modelValue ? formatDate(modelValue) : ''"
        :placeholder="placeholder"
        :disabled="props.disabled"
        @focus="onFocus"
        @blur="onInputBlur"
        @keydown.enter="onInputBlur"
      />
      <i class="icon_calendar_days" @click="onIconClick"></i>
    </div>
    <p v-if="error && errorMessage" class="lms_dp_error">
      <i class="mdi mdi-alert-outline"></i>
      {{ errorMessage }}
    </p>

    <Teleport to="body">
      <div v-if="open" class="lms_dp_overlay" @click="onOverlayClick">
        <div class="lms_dp_popup" :style="popupStyle" @click.stop>
          <div class="lms_dp_month">
            <!-- Day View -->
            <template v-if="viewMode === 'day'">
              <div class="lms_dp_month_header">
                <button type="button" class="lms_dp_nav" @click="prevMonth">
                  <i class="mdi mdi-chevron-left"></i>
                </button>
                <button type="button" class="lms_dp_month_title" @click="viewMode = 'month'">
                  {{ monthTitle }} 
                </button>
                <button type="button" class="lms_dp_nav" @click="nextMonth">
                  <i class="mdi mdi-chevron-right"></i>
                </button>
              </div>
              <div class="lms_dp_weekdays">
                <span v-for="(wd, i) in weekdays" :key="i" :class="{ sunday: i === 0 }">{{ wd }}</span>
              </div>
              <div class="lms_dp_days">
                <div
                  v-for="(day, i) in days"
                  :key="i"
                  class="lms_dp_day_cell"
                >
                  <span
                    v-if="day.date"
                    class="lms_dp_day"
                    :class="getDayClass(day)"
                    @click="onDayClick(day)"
                  >{{ day.date.getDate() }}</span>
                </div>
              </div>
            </template>

            <!-- Month View -->
            <template v-else-if="viewMode === 'month'">
              <div class="lms_dp_month_header">
                <button type="button" class="lms_dp_nav" @click="prevYear">
                  <i class="mdi mdi-chevron-left"></i>
                </button>
                <button type="button" class="lms_dp_month_title" @click="viewMode = 'year'">
                  {{ baseMonth.getFullYear() }}년
                </button>
                <button type="button" class="lms_dp_nav" @click="nextYear">
                  <i class="mdi mdi-chevron-right"></i>
                </button>
              </div>
              <div class="lms_dp_grid_4">
                <button
                  v-for="m in 12"
                  :key="m"
                  type="button"
                  class="lms_dp_grid_cell"
                  :class="{ selected: isSelectedMonth(m - 1) }"
                  @click="onMonthClick(m - 1)"
                >{{ m }}월</button>
              </div>
            </template>

            <!-- Year View -->
            <template v-else>
              <div class="lms_dp_month_header">
                <button type="button" class="lms_dp_nav" @click="prevDecade">
                  <i class="mdi mdi-chevron-left"></i>
                </button>
                <span class="lms_dp_month_title_static">
                  {{ decadeStart }} - {{ decadeStart + 11 }}
                </span>
                <button type="button" class="lms_dp_nav" @click="nextDecade">
                  <i class="mdi mdi-chevron-right"></i>
                </button>
              </div>
              <div class="lms_dp_grid_4">
                <button
                  v-for="y in yearList"
                  :key="y"
                  type="button"
                  class="lms_dp_grid_cell"
                  :class="{ selected: isSelectedYear(y) }"
                  @click="onYearClick(y)"
                >{{ y }}</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";

interface DayInfo {
  date: Date | null;
  inMonth: boolean;
  isSunday: boolean;
}

const props = defineProps({
  placeholder: { type: String, default: "YYYY-MM-DD" },
  error: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  closeOnOutsideClick: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  min: { type: Date, default: null },
});

const modelValue = defineModel<Date | null>({ default: null });

const open = ref(false);
const baseMonth = ref(new Date());
const popupStyle = ref<Record<string, string>>({});
const inputRef = ref<HTMLElement>();
const viewMode = ref<"day" | "month" | "year">("day");

const weekdays = ["일", "월", "화", "수", "목", "금", "토"];

const formatDate = (d: Date) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthTitle = computed(() => {
  const d = baseMonth.value;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
});

const prevMonth = () => {
  const d = new Date(baseMonth.value);
  d.setMonth(d.getMonth() - 1);
  baseMonth.value = d;
};

const nextMonth = () => {
  const d = new Date(baseMonth.value);
  d.setMonth(d.getMonth() + 1);
  baseMonth.value = d;
};

const prevYear = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() - 1);
  baseMonth.value = d;
};

const nextYear = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() + 1);
  baseMonth.value = d;
};

const decadeStart = computed(() => {
  const y = baseMonth.value.getFullYear();
  return Math.floor(y / 12) * 12;
});

const yearList = computed(() => {
  const start = decadeStart.value;
  return Array.from({ length: 12 }, (_, i) => start + i);
});

const prevDecade = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() - 12);
  baseMonth.value = d;
};

const nextDecade = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() + 12);
  baseMonth.value = d;
};

const isSelectedMonth = (m: number) => {
  return modelValue.value !== null
    && modelValue.value.getFullYear() === baseMonth.value.getFullYear()
    && modelValue.value.getMonth() === m;
};

const isSelectedYear = (y: number) => {
  return modelValue.value !== null && modelValue.value.getFullYear() === y;
};

const onMonthClick = (m: number) => {
  const d = new Date(baseMonth.value);
  d.setMonth(m);
  baseMonth.value = d;
  viewMode.value = "day";
};

const onYearClick = (y: number) => {
  const d = new Date(baseMonth.value);
  d.setFullYear(y);
  baseMonth.value = d;
  viewMode.value = "month";
};

const days = computed<DayInfo[]>(() => {
  const d = baseMonth.value;
  const year = d.getFullYear();
  const month = d.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const list: DayInfo[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, daysInPrevMonth - i);
    list.push({ date, inMonth: false, isSunday: date.getDay() === 0 });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    list.push({ date, inMonth: true, isSunday: date.getDay() === 0 });
  }
  const remaining = 42 - list.length;
  for (let i = 1; i <= remaining; i++) {
    const date = new Date(year, month + 1, i);
    list.push({ date, inMonth: false, isSunday: date.getDay() === 0 });
  }
  return list;
});

const isSameDay = (a: Date | null, b: Date | null) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const isDisabledDay = (date: Date | null) => {
  if (!date || !props.min) return false;
  return startOfDay(date).getTime() < startOfDay(props.min).getTime();
};

const getDayClass = (day: DayInfo) => {
  if (!day.date) return {};
  return {
    "not-in-month": !day.inMonth,
    sunday: day.isSunday && day.inMonth,
    selected: isSameDay(day.date, modelValue.value),
    today: isSameDay(day.date, new Date()),
    disabled: isDisabledDay(day.date),
  };
};

const onDayClick = (day: DayInfo) => {
  if (!day.date || props.disabled || isDisabledDay(day.date)) return;
  modelValue.value = day.date;
  setTimeout(() => { open.value = false; }, 150);
};

const parseInputDate = (str: string): Date | null => {
  const cleaned = str.replace(/[.\-\/]/g, "");
  if (cleaned.length !== 8 || !/^\d{8}$/.test(cleaned)) return null;
  const y = parseInt(cleaned.substring(0, 4));
  const m = parseInt(cleaned.substring(4, 6)) - 1;
  const d = parseInt(cleaned.substring(6, 8));
  const date = new Date(y, m, d);
  if (date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) return null;
  return date;
};

const onInputBlur = (e: Event) => {
  if (props.disabled) return;
  const input = e.target as HTMLInputElement;
  const val = input.value.trim();
  if (!val) {
    modelValue.value = null;
    return;
  }
  const parsed = parseInputDate(val);
  if (parsed && !isDisabledDay(parsed)) {
    modelValue.value = parsed;
    baseMonth.value = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
  } else {
    input.value = modelValue.value ? formatDate(modelValue.value) : "";
  }
};

const onOverlayClick = () => {
  if (open.value) open.value = false;
};

const onFocus = () => {
  if (!props.disabled) open.value = true;
};

const onIconClick = () => {
  if (!props.disabled) open.value = !open.value;
};

watch(open, async (val) => {
  if (val) {
    viewMode.value = "day";
    if (modelValue.value) {
      baseMonth.value = new Date(modelValue.value.getFullYear(), modelValue.value.getMonth(), 1);
    }
    await nextTick();
    if (inputRef.value) {
      const rect = inputRef.value.getBoundingClientRect();
      popupStyle.value = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
      };
    }
  }
});
</script>
